import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SeoArticleSchema from "@/components/SeoArticleSchema";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";
import { InsightKeyTakeaway } from "@/components/InsightKeyTakeaway";

const priorityFilters = [
  "healthcare",
  "budget",
  "climate",
  "community",
  "residency",
  "language",
  "travel",
  "taxes",
  "family proximity",
];

const decidingQuestions = [
  "What kind of life am I actually trying to create?",
  "Which tradeoffs would I accept more easily than others?",
  "What needs to hold up for this move to feel workable after the novelty fades?",
];

const renderBulletList = (items, prefix) => (
  <ul className="mt-5 space-y-3 text-sm leading-7 text-stone-600" data-testid={`${prefix}-list`}>
    {items.map((item, index) => (
      <li key={item} className="flex gap-3" data-testid={`${prefix}-item-${index + 1}`}>
        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-400/80" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function TheBiggestMistakePeopleMakeWhenChoosingACountry() {
  return (
    <div className="min-h-screen bg-[#f7f4ee] text-stone-900" data-testid="biggest-mistake-country-page">
      <SeoArticleSchema
        headline="The Biggest Mistake People Make When Choosing a Country"
        description="The biggest relocation mistake is comparing countries before you know which priorities should do the filtering. Learn a calmer way to decide."
        pathname="/insights/the-biggest-mistake-people-make-when-choosing-a-country"
        keywords={[
          "biggest mistake choosing a country abroad",
          "how to choose a country to move to",
          "relocation priorities before country comparison",
        ]}
      />
      <Header />

      <main className="px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="overflow-hidden rounded-[2rem] border border-stone-300/80 bg-white/92 shadow-[0_18px_48px_rgba(28,25,23,0.06)]" data-testid="biggest-mistake-hero-section">
            <div className="grid lg:grid-cols-[0.95fr_1.05fr]" data-testid="biggest-mistake-hero-grid">
              <div className="relative min-h-[300px] lg:min-h-full" data-testid="biggest-mistake-hero-image-panel">
                <img
                  src="https://images.unsplash.com/photo-1516546453174-5e1098a4b4af?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85"
                  alt="Notebook, glasses, and map laid out for thoughtful relocation planning"
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid="biggest-mistake-hero-image"
                />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(28,25,23,0.08)_0%,rgba(28,25,23,0.24)_100%)]" />
              </div>

              <div className="px-7 py-10 sm:px-10" data-testid="biggest-mistake-hero-copy">
                <div className="mb-5 h-1.5 w-16 rounded-full bg-amber-400/80" data-testid="biggest-mistake-hero-accent" />
                <div className="flex flex-wrap items-center gap-3" data-testid="biggest-mistake-hero-meta-row">
                  <p className="rc-section-kicker" data-testid="biggest-mistake-hero-kicker">Relocation insight</p>
                  <span className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-900" data-testid="biggest-mistake-hero-badge">New insight</span>
                  <span className="text-sm text-stone-500" data-testid="biggest-mistake-hero-reading-time">2 min read</span>
                </div>
                <h1 className="mt-4 font-manrope text-4xl text-stone-900 sm:text-5xl" data-testid="biggest-mistake-page-title">
                  The Biggest Mistake People Make When Choosing a Country
                </h1>
                <div className="mt-6 max-w-3xl space-y-4 text-base leading-8 text-stone-600" data-testid="biggest-mistake-page-intro">
                  <p>Most people think choosing a country is the first step. It usually isn’t.</p>
                  <p>The first step is understanding yourself well enough to know what should do the filtering.</p>
                  <p className="text-stone-800">Without that foundation, every destination stays in play too long and the process becomes exhausting.</p>
                </div>
              </div>
            </div>
          </section>

          <InsightKeyTakeaway
            testIdPrefix="biggest-mistake-key-takeaway"
            paragraphs={[
              "The biggest mistake people make when choosing a country is comparing destinations before they know which priorities should do the filtering.",
              "That mistake makes every article, ranking, and opinion feel equally important, even though the right answer usually depends on which tradeoffs matter most for your life, budget, healthcare needs, and long-term goals.",
            ]}
          />

          <section className="grid gap-6 lg:grid-cols-[1.08fr_0.92fr]" data-testid="biggest-mistake-core-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="biggest-mistake-problem-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="biggest-mistake-problem-title">What is the mistake, exactly?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="biggest-mistake-problem-copy">
                <p>Research is essential. The problem is beginning with countries before you know which priorities matter most to you.</p>
                <p>That makes every new article feel equally important, every opinion feel equally valid, and every destination feel like it still deserves more time.</p>
                <p className="text-stone-800">The overload usually comes from comparing too much before your own priorities have done any real sorting.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="biggest-mistake-priority-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="biggest-mistake-priority-title">Which priorities should filter first?</h2>
              <p className="mt-5 text-base leading-8 text-stone-600" data-testid="biggest-mistake-priority-copy">
                These factors are all important. The real question is which ones deserve the most weight for your life right now.
              </p>
              {renderBulletList(priorityFilters, "biggest-mistake-priority")}
            </article>
          </section>

          <section className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-10" data-testid="biggest-mistake-house-analogy-section">
            <h2 className="font-manrope text-2xl text-stone-900" data-testid="biggest-mistake-house-analogy-title">Why does this mistake create so much noise?</h2>
            <div className="mt-5 max-w-4xl space-y-4 text-base leading-8 text-stone-600" data-testid="biggest-mistake-house-analogy-copy">
              <p>Imagine asking which house you should buy without first talking about budget, commute, family needs, lifestyle, or whether this is a long-term home.</p>
              <p>Relocating abroad works the same way. Destinations make more sense only after your own life starts shaping the comparison.</p>
              <p className="text-stone-800">Trying to optimize every category at once usually leads to frustration. Deciding which categories matter most creates clarity.</p>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-2" data-testid="biggest-mistake-decision-grid">
            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="biggest-mistake-tradeoffs-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="biggest-mistake-tradeoffs-title">Why do tradeoffs matter more than perfection?</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-stone-600" data-testid="biggest-mistake-tradeoffs-copy">
                <p>There is no universally correct answer. Portugal is not the best country. Spain is not the best country. Mexico and Panama are not either.</p>
                <p>Each could be the right answer for someone. The challenge is seeing whether it is the right answer for you.</p>
                <p className="text-stone-800">People make more confident decisions when they understand which compromises they can accept—not when they keep hunting for perfection.</p>
              </div>
            </article>

            <article className="rounded-[2rem] border border-stone-300/80 bg-white/92 px-7 py-8 shadow-[0_18px_48px_rgba(28,25,23,0.05)] sm:px-8" data-testid="biggest-mistake-deciding-questions-section">
              <h2 className="font-manrope text-2xl text-stone-900" data-testid="biggest-mistake-deciding-questions-title">What should you ask before you compare countries?</h2>
              {renderBulletList(decidingQuestions, "biggest-mistake-deciding-questions")}
            </article>
          </section>

          <ContextualLinkPanel
            kicker="Where to go next"
            title="If this sharpened the problem, these are the best next pages from here."
            description="The next useful move is usually to reconnect the broader country-fit question, separate research from decision more clearly, and then turn your priorities into a smaller shortlist."
            testIdPrefix="biggest-mistake-next-steps"
            items={[
              {
                slug: "start-here",
                eyebrow: "Reconnect the foundation",
                title: "Why reading about countries isn’t the same as choosing one",
                description: "See why broad research and actual relocation decisions are different jobs requiring different questions.",
                ctaLabel: "Read the foundation piece",
                to: "/insights/why-reading-about-countries-isnt-the-same-as-choosing-one",
              },
              {
                slug: "decision-guide",
                eyebrow: "See the broader framework",
                title: "What country should I move to?",
                description: "Use a calmer framework for evaluating fit before you let destination research dominate the process.",
                ctaLabel: "Open the decision guide",
                to: "/insights/what-country-should-i-move-to",
              },
              {
                slug: "shortlist",
                eyebrow: "Turn priorities into action",
                title: "Build a realistic shortlist",
                description: "Move from insight into a smaller set of countries still worth comparing seriously.",
                ctaLabel: "Create the shortlist",
                to: "/insights/create-country-shortlist-for-moving-abroad",
              },
            ]}
          />

          <section className="rounded-[2rem] border border-stone-600/70 bg-stone-900 px-7 py-9 text-stone-50 shadow-[0_18px_48px_rgba(28,25,23,0.12)] sm:px-10" data-testid="biggest-mistake-cta-section">
            <div className="mb-5 h-1.5 w-14 rounded-full bg-amber-400/85" data-testid="biggest-mistake-cta-accent" />
            <p className="text-sm font-medium uppercase tracking-[0.18em] text-stone-300" data-testid="biggest-mistake-cta-kicker">Next step</p>
            <h2 className="mt-3 font-manrope text-3xl text-stone-50" data-testid="biggest-mistake-cta-title">Want your priorities to do the filtering for you?</h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-stone-300" data-testid="biggest-mistake-cta-copy">
              The assessment helps turn broad country interest into a more disciplined shortlist, so the comparison begins after your real priorities are already shaping the field.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4" data-testid="biggest-mistake-cta-actions">
              <Button asChild className="rounded-full bg-white px-6 text-stone-900 hover:bg-amber-100" data-testid="biggest-mistake-cta-button">
                <Link to="/auth">
                  Start your assessment
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}